import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import StoryTree from './StoryTree';
import { StoryNode, StoryEdge, StoryBranch } from '../services/api';
import NodeEditModal from './NodeEditModal';
import { storyApi } from '../services/api';

interface FullscreenStoryTreeProps {
  nodes: StoryNode[];
  edges: StoryEdge[];
  branches: StoryBranch[];
  isVisible: boolean;
  onClose: () => void;
  onNodeSave?: (nodeId: string, updatedData: Partial<StoryNode>) => void;
  onNodeDelete?: (nodeId: string) => void;
  onGenerateBranches?: (nodeId: string, path?: string[], allNodes?: StoryNode[], allEdges?: StoryEdge[], storyType?: string, storyStyle?: string, branchCount?: number) => void;
  isLoading?: boolean;
  initialSelectedNodeId?: string;
  initialStoryPath?: string[];
  branchCount: number;
  storyType: string;
  storyStyle: string;
}

const FullscreenStoryTree: React.FC<FullscreenStoryTreeProps> = ({
  nodes,
  edges,
  branches,
  isVisible,
  onClose,
  onNodeSave,
  onNodeDelete,
  onGenerateBranches,
  isLoading = false,
  initialSelectedNodeId,
  initialStoryPath = [],
  branchCount,
  storyType: initialStoryType,
  storyStyle: initialStoryStyle
}) => {
  const { t } = useTranslation();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(initialSelectedNodeId || null);
  const [storyPath, setStoryPath] = useState<string[]>(initialStoryPath);
  const [editingNode, setEditingNode] = useState<StoryNode | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [localNodes, setLocalNodes] = useState(nodes); // 新增本地节点state
  const [localEdges, setLocalEdges] = useState(edges); // 新增本地边state
  const [storyType, setStoryType] = useState(initialStoryType);
  const [storyStyle, setStoryStyle] = useState(initialStoryStyle);
  const [localBranchCount, setLocalBranchCount] = useState(branchCount);
  const [isGenerationModalVisible, setIsGenerationModalVisible] = useState(false);
  const [availableGenres, setAvailableGenres] = useState<string[]>([]);
  const [availableTones, setAvailableTones] = useState<string[]>([]);
  const [panelPosition, setPanelPosition] = useState<{ x: number; y: number } | null>(null); // 新增面板位置状态
  const storyTreeRef = useRef<HTMLDivElement>(null); // 新增StoryTree容器引用
  const [isGeneratingBranches, setIsGeneratingBranches] = useState(false); // 跟踪是否正在生成分支

  // 弹窗打开时动态获取类型和风格
  React.useEffect(() => {
    if (isGenerationModalVisible) {
      storyApi.getGenres().then(res => setAvailableGenres(res.genres)).catch(() => setAvailableGenres([]));
      storyApi.getTones().then(res => setAvailableTones(res.tones)).catch(() => setAvailableTones([]));
    }
  }, [isGenerationModalVisible]);

  // 保持本地state与props同步
  React.useEffect(() => { setLocalNodes(nodes); }, [nodes]);
  React.useEffect(() => { setLocalEdges(edges); }, [edges]);
  React.useEffect(() => { setStoryType(initialStoryType); }, [initialStoryType]);
  React.useEffect(() => { setStoryStyle(initialStoryStyle); }, [initialStoryStyle]);
  React.useEffect(() => { setLocalBranchCount(branchCount); }, [branchCount]);

  // 在组件变为可见时，设置初始选中状态
  React.useEffect(() => {
    if (isVisible && initialSelectedNodeId) {
      setSelectedNodeId(initialSelectedNodeId);
      setStoryPath(initialStoryPath);
    }
  }, [isVisible, initialSelectedNodeId, initialStoryPath]);

  // 监听isLoading状态变化，在生成分支完成后关闭Plot Details Panel
  React.useEffect(() => {
    if (isGeneratingBranches && !isLoading) {
      // 当isGeneratingBranches为true且isLoading变为false时，表示生成分支完成
      closePlotPanel();
      setIsGeneratingBranches(false); // 重置状态
    }
  }, [isLoading, isGeneratingBranches]);



  // 关闭面板的函数
  const closePlotPanel = () => {
    setSelectedNodeId(null);
    setStoryPath([]);
    setPanelPosition(null);
  };

  // 处理主容器点击事件
  const handleMainContainerClick = (e: React.MouseEvent) => {
    // 如果点击的是主容器本身（空白区域），关闭面板
    if (e.target === e.currentTarget) {
      closePlotPanel();
    }
  };

  // 构建从根节点到目标节点的路径
  const buildPathToNode = (targetNodeId: string): string[] => {
    const path: string[] = [];
    const visited = new Set<string>();
    
    // 找到根节点（没有入边的节点）
    const nodeIds = new Set(localNodes.map(n => n.id));
    const hasIncomingEdge = new Set(localEdges.map(e => e.to));
    const rootNodes = Array.from(nodeIds).filter(id => !hasIncomingEdge.has(id));
    
    // 深度优先搜索找到路径
    const dfs = (currentNodeId: string, currentPath: string[]): boolean => {
      if (visited.has(currentNodeId)) return false;
      visited.add(currentNodeId);
      
      const newPath = [...currentPath, currentNodeId];
      
      if (currentNodeId === targetNodeId) {
        path.push(...newPath);
        return true;
      }
      
      // 查找所有从当前节点出发的边
      const outgoingEdges = localEdges.filter(e => e.from === currentNodeId);
      for (const edge of outgoingEdges) {
        if (dfs(edge.to, newPath)) {
          return true;
        }
      }
      
      return false;
    };
    
    // 从每个根节点开始搜索
    for (const rootId of rootNodes) {
      visited.clear();
      if (dfs(rootId, [])) {
        break;
      }
    }
    
    return path;
  };

  const handleNodeClick = (nodeId: string, nodePosition?: { x: number; y: number }) => {
    setSelectedNodeId(nodeId);
    
    // 构建到选中节点的完整路径
    const pathToNode = buildPathToNode(nodeId);
    setStoryPath(pathToNode);
    
    // 设置面板位置到节点附近
    if (nodePosition && storyTreeRef.current) {
      const containerRect = storyTreeRef.current.getBoundingClientRect();
      const panelWidth = 420;
      const panelHeight = 400; // 估算面板高度
      
      // 计算面板位置，确保不超出容器边界
      let x = nodePosition.x + 60; // 节点右侧偏移
      let y = nodePosition.y - panelHeight / 2; // 垂直居中
      
      // 边界检查
      if (x + panelWidth > containerRect.width) {
        x = nodePosition.x - panelWidth - 60; // 放到节点左侧
      }
      if (y < 20) {
        y = 20;
      }
      if (y + panelHeight > containerRect.height - 20) {
        y = containerRect.height - panelHeight - 20;
      }
      
      setPanelPosition({ x, y });
    }
    
    // Find related branch or create a temporary one from node data
    let relatedBranch = branches.find(branch => branch.id === nodeId);
    
    // If no branch found, create one from node data
    if (!relatedBranch) {
      const node = localNodes.find(n => n.id === nodeId);
      if (node) {
        relatedBranch = {
          id: node.id,
          title: node.title || node.label || '未命名剧情',
          description: node.content || node.title || node.label || '暂无详细描述',
          created_at: new Date().toISOString()
        };
      }
    }
  };

  const handleNodeDoubleClick = (nodeId: string) => {
    // 可以添加双击编辑功能
  };

  const handleNodeEdit = (nodeId: string) => {
    const node = localNodes.find(n => n.id === nodeId);
    if (node) {
      setEditingNode(node);
      setIsEditModalVisible(true);
    }
  };

  const handleNodeSave = (nodeId: string | null, updatedData: Partial<StoryNode>) => {
    if (onNodeSave) {
      Promise.resolve(onNodeSave(nodeId!, updatedData)).finally(() => {
        setIsEditModalVisible(false);
        setEditingNode(null);
      });
    } else {
      setIsEditModalVisible(false);
      setEditingNode(null);
    }
  };

  const handleModalCancel = () => {
    setIsEditModalVisible(false);
    setEditingNode(null);
  };

  const handleAddChildNode = (parentId: string, nodeData: { label: string; title: string; description: string; content: string }) => {
    const newId = `node_${Date.now()}_${Math.floor(Math.random()*10000)}`;
    setLocalNodes(prev => [...prev, { id: newId, ...nodeData }]);
    setLocalEdges(prev => [...prev, { from: parentId, to: newId }]);
  };

  // 在组件内添加isRoot工具函数
  const isRoot = (id: string) => {
    const nodeIds = new Set(localNodes.map(n => n.id));
    const hasIncomingEdge = new Set(localEdges.map(e => e.to));
    const rootNodes = Array.from(nodeIds).filter(id => !hasIncomingEdge.has(id));
    return rootNodes.includes(id);
  };

  const handleExport = () => {
    if (!selectedNodeId) return;

    const path = buildPathToNode(selectedNodeId);
    if (path.length === 0) return;

    let markdownContent = `# ${t('export.title')}\n\n`;
    markdownContent += `## ${t('export.storyPath')}\n\n`;
    
    const pathNodes = path.map(nodeId => localNodes.find(n => n.id === nodeId));

    markdownContent += pathNodes.map(node => node?.label || 'Unknown').join(' → ') + '\n\n';

    markdownContent += `## ${t('export.plotDetails')}\n\n`;

    pathNodes.forEach((node, index) => {
      if (node) {
        markdownContent += `### ${t('export.step', { count: index + 1 })}: ${node.title || node.label}\n\n`;
        markdownContent += `${node.content || t('export.noContent')}\n\n`;
      }
    });

    const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const storyTitle = localNodes.find(n => isRoot(n.id))?.title || 'story';
    link.download = `${storyTitle.replace(/ /g, '_')}_path.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const selectedNode = localNodes.find(n => n.id === selectedNodeId);

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid rgba(76, 201, 240, 0.3)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2 style={{
            margin: 0,
            color: '#ffffff',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            <span role="img" aria-label="tree">🌳</span> {t('storyTree.title')}
          </h2>
          
          {/* Control Options Button */}
          <button
            onClick={() => setIsGenerationModalVisible(true)}
            style={{
              background: 'rgba(76, 201, 240, 0.2)',
              border: '1px solid rgba(76, 201, 240, 0.5)',
              borderRadius: '8px',
              padding: '8px 16px',
              color: '#4cc9f0',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(76, 201, 240, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(76, 201, 240, 0.2)';
            }}
          >
            ⚙️ {t('storyTree.controlOptions')}
          </button>

          {/* Loading Indicator */}
          {isLoading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(76, 201, 240, 0.1)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#4cc9f0',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(76, 201, 240, 0.3)',
                borderTop: '2px solid #4cc9f0',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              {t('storyTreePanel.generatingBranches', { count: localBranchCount })}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255, 107, 107, 0.2)',
            border: '1px solid rgba(255, 107, 107, 0.5)',
            borderRadius: '8px',
            padding: '8px 16px',
            color: '#ff6b6b',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 107, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 107, 107, 0.2)';
          }}
        >
          ✕ {t('storyTree.fullscreen.close')}
        </button>
      </div>

      {/* Main Content */}
      <div 
        onClick={handleMainContainerClick}
        style={{
          flex: 1,
          display: 'flex',
          position: 'relative',
          height: 'calc(97vh - 100px)', // 减去header的高度
          marginBottom: '50px'
        }}>
        {/* 添加一个透明层，用于捕获点击事件 */}
        {selectedNode && panelPosition && (
          <div 
            onClick={closePlotPanel}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1
            }}
          />
        )}
        {/* Story Tree - Full Width */}
        <div ref={storyTreeRef} style={{
          flex: 1,
          padding: '20px',
          paddingRight: '20px',
          paddingBottom: '20px', // 添加底部边距
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
          zIndex: 0 // 确保 StoryTree 位于透明层下方
        }}>
          <StoryTree
            nodes={localNodes}
            edges={localEdges}
            onNodeClick={handleNodeClick}
            onNodeDoubleClick={handleNodeDoubleClick}
            onNodeEdit={handleNodeEdit}
            onNodeDelete={onNodeDelete}
            selectedNodeId={selectedNodeId}
            storyPath={storyPath}
            onAddChildNode={handleAddChildNode} // 新增
            // isLoading={isLoading} // 移除此行，不再传递isLoading给StoryTree
          />
        </div>

        {/* Plot Details Panel - Dynamic Position */}
        {selectedNode && panelPosition && (
          <div 
            onClick={(e) => e.stopPropagation()} // 阻止点击事件冒泡
            style={{
              position: 'absolute',
              top: `${panelPosition.y}px`,
              left: `${panelPosition.x}px`,
              width: '420px',
              height: 'auto', // 自动高度
              maxHeight: 'calc(100% - 40px)', // 限制最大高度
              background: 'linear-gradient(145deg, rgba(26, 42, 108, 0.95), rgba(67, 97, 238, 0.1))',
              border: '2px solid rgba(76, 201, 240, 0.4)',
              borderRadius: '16px',
              padding: '24px',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              zIndex: 2 // 确保面板显示在透明层之上
            }}>
            {/* Close Button for Panel */}
             <button
               onClick={closePlotPanel}
               style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '16px',
                cursor: 'pointer',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }}
            >
              ✕
            </button>

            {/* Panel Header */}
            <div style={{
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '2px solid rgba(76, 201, 240, 0.3)',
              background: 'linear-gradient(90deg, rgba(76, 201, 240, 0.1), transparent)',
              borderRadius: '8px',
              padding: '12px 16px'
            }}>
              <h3 style={{
                margin: 0,
                color: '#ffffff',
                fontSize: '20px',
                fontWeight: 'bold',
                opacity: 0.95,
                marginBottom: '8px',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>
                <span role="img" aria-label="branch">🌱</span> {isRoot(selectedNode.id) ? t('storyTree.rootNode') : (selectedNode.title || selectedNode.label)}
              </h3>
              {storyPath.length > 1 && (
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)'
                }}>
                  {t('storyTree.pathLength')}: {storyPath.length} {t('storyTree.nodes')}
                </div>
              )}
            </div>

            {/* Story Path Display */}
            {storyPath.length > 1 && (
              <div style={{
                background: 'rgba(67, 97, 238, 0.1)',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid rgba(67, 97, 238, 0.3)'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#4cc9f0',
                  marginBottom: '8px'
                }}>
                  📍 {t('storyTreePanel.storyPath')}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  alignItems: 'center'
                }}>
                  {storyPath.map((nodeId, index) => {
                    const node = localNodes.find(n => n.id === nodeId);
                    return (
                      <React.Fragment key={nodeId}>
                        <span style={{
                          background: index === storyPath.length - 1 
                            ? 'rgba(76, 201, 240, 0.3)' 
                            : 'rgba(255, 255, 255, 0.1)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          border: index === storyPath.length - 1 
                            ? '1px solid rgba(76, 201, 240, 0.5)' 
                            : '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                          {node?.label || nodeId.slice(0, 8)}
                        </span>
                        {index < storyPath.length - 1 && (
                          <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>→</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Branch Content 可滚动区域 */}
            <div style={{
              flex: 1,
              minHeight: '100px', // 最小高度
              maxHeight: '30vh', // 最大高度
              overflowY: 'auto',
              marginBottom: '16px',
              background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(76, 201, 240, 0.1))',
              padding: '20px',
              borderRadius: '12px',
              border: '2px solid rgba(67, 97, 238, 0.4)',
              boxShadow: 'inset 0 2px 8px rgba(67, 97, 238, 0.2)'
            }}>
              <div style={{
                fontSize: '15px',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.7',
                marginBottom: selectedNode && selectedNode.created_at ? '12px' : '0',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>
                {selectedNode.content || selectedNode.title || selectedNode.label}
              </div>
              {selectedNode?.created_at && (
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.5)'
                }}>
                  {t('storyTreePanel.createdAt')}: {new Date(selectedNode.created_at).toLocaleString()}
                </div>
              )}
            </div>

            <div style={{ marginTop: 'auto', display: 'flex', gap: '12px' }}>
              {/* Generate Branches Button */}
              {onGenerateBranches && selectedNodeId && (
                  <button
                    onClick={() => {
                      // 直接使用当前设置调用接口
                      setIsGeneratingBranches(true);
                      onGenerateBranches(selectedNodeId, storyPath, localNodes, localEdges, storyType, storyStyle, localBranchCount);
                    }}
                    disabled={isLoading}
                    style={{
                      background: isLoading 
                        ? 'rgba(76, 201, 240, 0.3)' 
                        : 'linear-gradient(45deg, #4361ee, #4cc9f0)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isLoading ? 0.6 : 1,
                      boxShadow: !isLoading ? '0 2px 8px rgba(67, 97, 238, 0.3)' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center',
                      flex: 3
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.background = 'linear-gradient(45deg, #5a73f5, #5dd9f5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.background = 'linear-gradient(45deg, #4361ee, #4cc9f0)';
                      }
                    }}
                  >
                    <span>🌱</span>
                    {isLoading ? t('storyTreePanel.generatingBranches', { count: localBranchCount }) : t('storyTreePanel.generateBranches', { count: localBranchCount })}
                  </button>
                )}

                {/* Export Button */}
              <button
                onClick={handleExport}
                style={{
                  background: 'rgba(76, 201, 240, 0.2)',
                  border: '1px solid rgba(76, 201, 240, 0.5)',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  color: '#4cc9f0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flex: 1
                }}
              >
                <span role="img" aria-label="export">📄</span> {t('storyTreePanel.export')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Generation Controls Modal */}
      {isGenerationModalVisible && (
        <div 
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setIsGenerationModalVisible(false)}
        >
          <div 
            className="modal-content"
            style={{
              background: 'linear-gradient(135deg, rgba(26, 42, 108, 0.95) 0%, rgba(58, 97, 134, 0.95) 100%)',
              border: '1px solid rgba(76, 201, 240, 0.3)',
              borderRadius: '16px',
              padding: '32px 28px',
              maxWidth: '420px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              position: 'relative'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: '#4cc9f0', fontWeight: 'bold', fontSize: '18px', marginBottom: '18px', textAlign: 'center' }}>
              ⚙️ {t('storyTreePanel.generationControls')}
            </h3>
            <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
              {/* Story Type */}
              <div>
                <label style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', display: 'block', marginBottom: '4px' }}>{t('story.type')}</label>
                <select value={storyType} onChange={e => setStoryType(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', background: 'rgba(0,0,0,0.3)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <option value="">{t('storyInput.genre.placeholder')}</option>
                  {availableGenres.map(g => (
                    <option key={g} value={g}>{t(`story.types.${g.toLowerCase()}`)}</option>
                  ))}
                </select>
              </div>
              {/* Story Style */}
              <div>
                <label style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', display: 'block', marginBottom: '4px' }}>{t('story.style')}</label>
                <select value={storyStyle} onChange={e => setStoryStyle(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', background: 'rgba(0,0,0,0.3)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <option value="">{t('storyInput.tone.placeholder')}</option>
                  {availableTones.map(tone => (
                    <option key={tone} value={tone}>{t(`story.styles.${tone.toLowerCase()}`)}</option>
                  ))}
                </select>
              </div>
              {/* Branch Count */}
              <div>
                <label style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', display: 'block', marginBottom: '4px' }}>{t('story.branchCount')}</label>
                <input type="number" min="1" max="5" value={localBranchCount} onChange={e => setLocalBranchCount(parseInt(e.target.value, 10))} style={{ width: '100%', padding: '8px', borderRadius: '4px', background: 'rgba(0,0,0,0.3)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setIsGenerationModalVisible(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(76, 201, 240, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('nodeModal.edit.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      <NodeEditModal
        node={editingNode}
        isVisible={isEditModalVisible}
        onSave={handleNodeSave}
        onCancel={handleModalCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default FullscreenStoryTree;